'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import type { RouterOutputs } from '@package/api'
import type { SelectOrganization } from '@package/drizzle/schema'
import { Button, NumberFlow, Skeleton, toast } from '@ui/web/components'
import { WhatsappIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { WHATSAPP_LINK } from '@helper/utils/core'
import { buildUrl, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { SeatAssignmentValue } from '../../../lib'
import { useSharedTRPC } from '../../../context'
import { AssignSeatDialog, partitionOfferableChildren, truncateDefaultSeatAssignment, VoucherList } from '../../../lib'
import { AnimatedCurrency } from './animated-currency'
import { CheckoutCard, CheckoutReveal } from './checkout-card'
import { ADD_ON_MINIMUM_SEATS } from './util'

/** Narrows `listChildren`'s optional seat-flavour fields to the shape the seat helpers require. */
function toFlavouredChild(child: {
  id: string
  heldSeatId: string | null
  heldSeatSourceType?: string | null
  heldSeatOrganizationId?: string | null
  heldSeatIsCommercial?: boolean
  premiumId: string | null
  familyPremiumId: string | null
}) {
  return {
    id: child.id,
    heldSeatId: child.heldSeatId,
    heldSeatSourceType: child.heldSeatSourceType ?? null,
    heldSeatOrganizationId: child.heldSeatOrganizationId ?? null,
    heldSeatIsCommercial: child.heldSeatIsCommercial ?? false,
    premiumId: child.premiumId,
    familyPremiumId: child.familyPremiumId,
  }
}

interface AddOnFormProps {
  currentMembership: RouterOutputs['myOrganization']['getSeatPools'][number]
  /**
   * The purchase invoice behind the pool, when there is one.
   *
   * NULL for a DIRECT-PROVISIONED pool — an offline-paid `direct_paid` entitlement is commercial and
   * takes add-on seats like any purchase, but it was never bought through checkout, so no invoice
   * row exists and `invoice.getInvoice` throws NOT_FOUND for it. Everything this form took from the
   * invoice now comes from the quote, which prices such a pool from the public card
   * (`resolveLegacyAddOnRate`); the invoice is only a nicety on top.
   */
  invoice: RouterOutputs['invoice']['getInvoice'] | null
  organizationData: Pick<SelectOrganization, 'id' | 'name'>
  initialCount: number | null
  type: 'web' | 'school'
  onNavigate: (url: string) => void
}

/**
 * "Tambah kursi" — seats bought mid-term, joining every pool the school has already paid for.
 *
 * The quote comes from the server (`price.getAddOnQuote`) rather than being multiplied out here.
 * Under a renewed subscription there is more than one pool to join, each bought at its own rate, so
 * a client-side `rate x days x seats` cannot reach the same number the invoice will — and the one
 * the buyer reads has to be the one they are charged.
 *
 * NO TIER OR TERM SAVINGS HERE, unlike the NEW form. An add-on has no package to choose: it
 * inherits the term and the rate of the pools it joins, so there is no cheaper alternative to
 * point at. The only reduction that exists is an `ADD_ON`-scoped voucher.
 */
export function AddOnForm({
  currentMembership,
  invoice,
  organizationData,
  initialCount,
  type,
  onNavigate,
}: AddOnFormProps) {
  const trpc = useSharedTRPC()

  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  /**
   * Who these seats are for, and how many are bought empty — the same shape the NEW checkout uses,
   * because it is the same decision: an add-on exists to cover students the organization could not
   * cover before.
   *
   * `null` until the roster settles it. A placeholder count here would silence the default below and
   * quote one seat to a school with thirty uncovered students.
   */
  const [assignment, setAssignment] = useState<SeatAssignmentValue | null>(null)
  // Settled by whichever path gets there first — the default below, or the buyer confirming the
  // dialog. Once true the roster effect never recomputes over a choice already made.
  const assignmentSettledRef = useRef(false)
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null)

  const isAssignmentUnsettled = assignment === null

  const { data: childrenData, isError: isErrorChildren } = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
      includeSeatFlavour: true,
    }),
    enabled: isAssignmentUnsettled,
  })

  useEffect(() => {
    if (assignmentSettledRef.current) return

    let next: SeatAssignmentValue | null = null

    if (isErrorChildren) {
      // The roster could not be read, so there is nobody to default to. Settle on the bare count
      // rather than leaving Konfirmasi disabled while the request keeps failing.
      next = { childIds: [], emptySeats: Math.max(ADD_ON_MINIMUM_SEATS, initialCount ?? ADD_ON_MINIMUM_SEATS) }
    } else if (childrenData) {
      const children = childrenData.data.map(toFlavouredChild)

      if (initialCount !== null) {
        // Deep link from "Kelola kursi", which already knows how many seats were short: the same
        // offerable selection truncated to that number, remainder as empty seats.
        next = truncateDefaultSeatAssignment({ children, organizationId: organizationData.id, total: initialCount })
      } else {
        /**
         * Everyone this purchase can seat — students holding no seat at all, plus holders of this
         * organization's own free-trial seat, which fulfilment releases.
         *
         * NOT the NEW form's ladder: that falls back to a trial-pool mirror and then to eight seats,
         * which are opening offers for a school buying its first plan. A school topping up already
         * has a plan, so with nobody left to cover the honest default is the smallest order it can
         * place — one seat, parked for the next student to enrol.
         */
        const { eligibleIds, ownTrialIds } = partitionOfferableChildren(children, organizationData.id)
        const childIds = [...eligibleIds, ...ownTrialIds]

        next = childIds.length > 0 ? { childIds, emptySeats: 0 } : { childIds: [], emptySeats: ADD_ON_MINIMUM_SEATS }
      }
    }

    if (!next) return

    setAssignment(next)
    assignmentSettledRef.current = true
  }, [childrenData, isErrorChildren, initialCount, organizationData.id])

  const count = useMemo(
    () => (assignment === null ? null : assignment.childIds.length + assignment.emptySeats),
    [assignment],
  )

  /**
   * The seat count the quote is asked for, trailing the stepper by a beat.
   *
   * Holding the plus button or typing "15" fires a request per intermediate number otherwise, and
   * every one of them is a round trip whose answer is thrown away.
   */
  const [debouncedCount] = useDebouncedValue(count, { wait: 300 })

  // Re-quoted on every seat change — it is one query and the arithmetic belongs to the server.
  const quoteQuery = useQuery({
    ...trpc.price.getAddOnQuote.queryOptions({
      membershipId: currentMembership.id,
      seats: debouncedCount ?? ADD_ON_MINIMUM_SEATS,
    }),
    // Nothing to quote until the roster has settled how many seats this order is.
    enabled: debouncedCount !== null,
    /**
     * Seats are part of the query key, so each count is a cache entry of its own and every press
     * landed on an empty one — which is why the card blanked into skeletons on every click. Keeping
     * the previous answer on screen while the new one is fetched makes the figures update in place.
     */
    placeholderData: keepPreviousData,
  })

  const quote = quoteQuery.data
  const totalPrice = quote?.totalPrice ?? 0
  // True only while a quote the buyer has never seen is loading — the first one. A re-quote keeps
  // the old figures up, so the skeletons must not come back for it.
  const isQuoteLoading = quoteQuery.isPending
  // The figures on screen belong to a different seat count until this settles.
  const isRequoting = quoteQuery.isFetching || debouncedCount !== count || count === null

  // Add-on vouchers are their own scope: a package code is priced against a full term and would
  // take far too much off a few pro-rata days, so the server refuses it outright.
  const { data: vouchersData } = useQuery(trpc.price.getActiveVouchers.queryOptions({ scope: 'ADD_ON' }))

  const selectedVoucher = vouchersData?.find((voucher) => voucher.id === selectedVoucherId) ?? null

  /**
   * The discount as the form shows it. `computeCheckoutCharge` resolves the voucher again and that
   * result is what is charged — this exists so the card can show a number without a round trip per
   * seat pressed.
   */
  const voucherDiscount = (() => {
    if (!selectedVoucher) return 0

    const value = Number(selectedVoucher.value)

    const raw =
      selectedVoucher.type === 'PERCENTAGE'
        ? selectedVoucher.maximumValue !== null && selectedVoucher.maximumValue !== undefined
          ? Math.min(totalPrice * (value / 100), selectedVoucher.maximumValue)
          : totalPrice * (value / 100)
        : value

    return Math.floor(Math.min(raw, totalPrice))
  })()

  const amountPaid = Math.max(0, totalPrice - voucherDiscount)

  // A voucher chosen at fifteen seats can stop qualifying at five. Dropped rather than carried into
  // a submit the server would refuse.
  useEffect(() => {
    if (!selectedVoucher) return
    if (selectedVoucher.minimumTransaction !== null && totalPrice < selectedVoucher.minimumTransaction) {
      setSelectedVoucherId(null)
    }
  }, [selectedVoucher, totalPrice])

  const checkoutAddOnMutation = useMutation(
    trpc.invoice.createInvoice.mutationOptions({
      onSuccess: (data) => {
        if (data.xenditInvoice) {
          onNavigate(data.xenditInvoice.invoiceUrl)
        } else {
          const successUrl = buildUrl(type, '/checkout/success', {
            invoice_id: data.invoice.id,
            type: 'ADD-ON',
            membership_id: currentMembership.id,
          }).href
          onNavigate(successUrl)
        }
      },
    }),
  )

  useEffect(() => {
    if (checkoutAddOnMutation.isError) {
      toast.error('Ada error yang terjadi saat ingin mencoba bayar')
    }
  }, [checkoutAddOnMutation.isError])

  const isPending = checkoutAddOnMutation.isPending

  const parts = quote?.parts ?? []
  /**
   * The pool these seats join TODAY, which is the one the card is quoted from.
   *
   * `find`, not `parts[0]`: the chain is ordered by start date, so the live pool leads it in the
   * ordinary case — but a school adding seats against a pool that has not opened yet has no live
   * part at all, and reading index 0 blindly would label a future rate as the current one.
   */
  const livePart = parts.find((part) => !part.isFuture) ?? parts[0]
  /**
   * The monthly rate shown at the top of the card.
   *
   * Read from the QUOTE, not from the invoice: the quote's live part already carries the rate that
   * pool is charged at whatever its provenance — the invoice's `perSeatPrice` for a purchase, the
   * public-card fallback for a direct-provisioned one.
   */
  const perSeatMonthly = livePart?.perSeatPrice ?? invoice?.price?.price ?? null
  /**
   * The PRO-RATA day rate the live pool actually charges — `perSeatPrice / 30`, resolved server
   * side so the header and the breakdown below cannot disagree.
   *
   * Shown beside the monthly rate because the monthly one is not what this order pays: seats joining
   * mid-term are billed by the day, and a card headed only "Rp19.500 per kursi per bulan" reads as
   * a bill for a whole month.
   */
  const perSeatDaily = livePart?.perSeatPerDay ?? null

  /**
   * The window these seats actually cover: the first pool they join to the last one's end.
   *
   * NOT the live pool's own window. An add-on placed after a renewal joins every pool the school
   * has paid for, so a school that renewed to 2028 is buying cover to 2028 today — and the card
   * used to say "x 365 sisa hari", which is the live pool's slice and reads as the whole purchase
   * ending a year before it does. The Total was already pricing the full chain, so the line and
   * the figure beneath it disagreed.
   */
  const coversFrom = parts[0]?.startDate ?? null
  const coversUntil = quote?.coversUntil ?? null
  const coveredDays =
    coversFrom && coversUntil
      ? Math.max(0, Math.round((new Date(coversUntil).getTime() - new Date(coversFrom).getTime()) / 86_400_000))
      : null

  return (
    <section className='srd:text-neutral-black srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
      <section className='srd:flex srd:w-full srd:flex-wrap srd:items-start srd:justify-center srd:gap-8'>
        <section className='srd:flex srd:w-full srd:max-w-[550px] srd:flex-col srd:gap-y-4'>
          <CheckoutCard
            organizationName={organizationData.name}
            title='Premium'
            primaryLine={
              perSeatMonthly === null ? undefined : (
                <>
                  <AnimatedCurrency value={perSeatMonthly} /> per kursi per bulan
                </>
              )
            }
            secondaryLine={
              perSeatDaily === null ? undefined : (
                <>
                  <AnimatedCurrency value={perSeatDaily} /> per kursi per hari
                </>
              )
            }
          >
            {/* ONE line, whatever the school has bought before. The breakdown used to run a line per
                pool the seats join plus a sentence explaining why a renewed school saw more than
                one — which made the card look different to two buyers placing the same order. The
                live pool's pro-rata rate is the rate this order is actually quoted at; the window it
                all adds up to is "Kursi aktif sampai" below, and the money is the total. */}
            <section className='srd:border-outline-variant srd:flex srd:flex-col srd:gap-y-1 srd:rounded-lg srd:border srd:p-4'>
              <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>Tambah kursi</p>

              {isQuoteLoading ? (
                <Skeleton className='srd:h-5 srd:w-3/4' />
              ) : (
                livePart && (
                  <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                    <AnimatedCurrency
                      value={livePart.perSeatPerDay ?? 0}
                      className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}
                    />{' '}
                    {/* Days across the WHOLE chain, not the live pool's own slice. An add-on placed
                        after a renewal joins every pool the school has paid for, so a school renewed
                        to 2028 is buying cover to 2028 today — and this line read "365 sisa hari",
                        which is where the live pool stops and a year short of where the seats do. */}
                    per kursi x {coveredDays ?? livePart.days} sisa hari
                  </p>
                )
              )}

              {coversUntil && (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  Kursi aktif sampai {formatDate(coversUntil, { dateStyle: 'long' })}
                </p>
              )}
            </section>

            <section className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total kursi</p>

              {/* The count is a figure, not a control — only "Ubah" opens the dialog. Same shape as
                  the NEW checkout. */}
              <div className='srd:flex srd:items-center srd:gap-x-2'>
                {count === null ? (
                  <Skeleton className='srd:h-6 srd:w-8' />
                ) : (
                  <NumberFlow
                    value={count}
                    className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}
                  />
                )}

                <Button size='sm' disabled={isPending || count === null} onClick={() => setAssignDialogOpen(true)}>
                  Ubah
                </Button>
              </div>
            </section>

            <AssignSeatDialog
              open={assignDialogOpen}
              onOpenChange={setAssignDialogOpen}
              type={type}
              organizationId={organizationData.id}
              value={assignment}
              onConfirm={(value: SeatAssignmentValue) => {
                assignmentSettledRef.current = true
                setAssignment(value)
                // A voucher qualifying at fifteen seats may not at five; the effect above drops it
                // once the new quote lands.
              }}
            />

            <CheckoutReveal show={voucherDiscount > 0}>
              <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                  Diskon promo
                </p>

                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                  (
                  <AnimatedCurrency
                    value={voucherDiscount}
                    className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}
                  />
                  )
                </p>
              </section>
            </CheckoutReveal>

            <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

              {isQuoteLoading ? (
                <Skeleton className='srd:h-7 srd:w-32' />
              ) : (
                <AnimatedCurrency
                  value={amountPaid}
                  className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}
                />
              )}
            </section>

            {checkoutAddOnMutation.error && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                {checkoutAddOnMutation.error.message}
              </p>
            )}
          </CheckoutCard>
        </section>

        <section className='srd:flex srd:w-full srd:max-w-[510px] srd:flex-col srd:gap-y-4'>
          <VoucherList
            className='srd:w-full'
            priceBeforeVoucher={totalPrice}
            onSelectVoucher={(voucher) => setSelectedVoucherId(voucher?.id ?? null)}
            selectedVoucherId={selectedVoucherId}
            // An add-on has no rate-card row to narrow against; `scope` is what decides the list.
            selectedPriceId={invoice?.price?.id ?? ''}
            scope='ADD_ON'
            type={type}
            disabled={isPending}
          />

          <section className='srd:border-outline-variant/50 srd:flex srd:w-full srd:flex-col srd:items-center srd:gap-y-2 srd:rounded-xl srd:border srd:p-4'>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black srd:text-center')}>
              Merencanakan untuk banyak murid atau rombongan belajar? Kami dapat membantumu dapatkan nilai terbaik
              untukmu.
            </p>
            <Button asChild variant='ghost' size='sm' className='srd:text-neutral-black'>
              <a href={WHATSAPP_LINK} target='_blank' rel='noreferrer'>
                <WhatsappIcon className='srd:mr-2 srd:size-4 srd:shrink-0' />
                Mari berdiskusi
              </a>
            </Button>
          </section>
        </section>
      </section>

      <div className='srd:bg-neutral-white srd:fixed srd:bottom-0 srd:left-0 srd:h-fit srd:w-full srd:shadow-inner srd:z-50'>
        <div className='srd:mx-auto srd:flex srd:w-full srd:max-w-[1440px] srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-6 srd:gap-y-2 srd:px-2 srd:py-4'>
          <section className='srd:flex srd:flex-col srd:gap-y-0'>
            <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

            {isQuoteLoading ? (
              <Skeleton className='srd:h-7 srd:w-32' />
            ) : (
              <AnimatedCurrency
                value={amountPaid}
                className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}
              />
            )}

            {voucherDiscount > 0 && (
              <p
                className={cn(
                  typographyVariants({ variant: 'body-md' }),
                  'srd:text-purple-100 srd:flex srd:items-center srd:gap-x-1',
                )}
              >
                Total hemat
                <AnimatedCurrency
                  value={voucherDiscount}
                  className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-purple-100')}
                />
              </p>
            )}
          </section>

          <Button
            variant='gradient'
            size='lg'
            loading={isPending}
            // Held while the quote catches up with the seat count: the figures on screen are still
            // the previous count's, and Konfirmasi must never submit a total the buyer has not seen.
            disabled={
              isPending || isQuoteLoading || isRequoting || !quote || count === null || count < ADD_ON_MINIMUM_SEATS
            }
            onClick={() => {
              if (count === null || assignment === null) return

              checkoutAddOnMutation.mutate({
                organizationId: organizationData.id,
                type: 'ADD-ON',
                seats: count,
                totalPrice,
                amountPaid,
                // The students these seats are for. Re-validated server-side, and applied again at
                // fulfilment by `mintAddOnSeats` — a child seated by a parent in between simply
                // drops out and the seat is minted unassigned.
                assignToChildIds: assignment.childIds,
                // Null for a direct-provisioned pool. `Invoice.priceId` is nullable and the ADD-ON
                // charge never reads it — the chain is priced from the pools themselves.
                priceId: invoice?.price?.id,
                voucherId: selectedVoucherId ?? undefined,
                withXendit: amountPaid > 0,
                xenditConfig: {
                  membershipId: currentMembership.id,
                },
              })
            }}
          >
            Konfirmasi
          </Button>
        </div>
      </div>
    </section>
  )
}
